import React from "react";
import { Box, Typography, TextField, Button, Stack, CircularProgress } from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

export interface CreditCardData {
  holderName: string;
  number: string;
  expiryMonth: string;
  expiryYear: string;
  ccv: string;
  cpfCnpj: string;
  holderEmail: string;
  holderPhone: string;
  postalCode: string;
  addressNumber: string;
}

interface CreditCardFormProps {
  onSubmit: (data: CreditCardData) => void;
  onBack: () => void;
  isLoading: boolean;
}

const schema = yup.object({
  holderName: yup.string().required("Nome é obrigatório"),
  number: yup
    .string()
    .required("Número é obrigatório")
    .test("card-digits", "Número deve ter 16 dígitos", (val) => {
      if (!val) return false;
      return val.replace(/\s/g, "").length === 16;
    }),
  expiryMonth: yup
    .string()
    .required("Mês obrigatório")
    .matches(/^(0[1-9]|1[0-2])$/, "Mês inválido (01-12)"),
  expiryYear: yup
    .string()
    .required("Ano obrigatório")
    .matches(/^20\d{2}$/, "Ano inválido")
    .test("year-future", "Ano expirado", (val) => {
      if (!val || val.length < 4) return false;
      return parseInt(val) >= new Date().getFullYear();
    }),
  ccv: yup
    .string()
    .required("CVV obrigatório")
    .matches(/^\d{3,4}$/, "CVV deve ter 3 ou 4 dígitos"),
  cpfCnpj: yup
    .string()
    .required("CPF é obrigatório")
    .test("cpf-digits", "CPF deve ter 11 dígitos", (val) => {
      if (!val) return false;
      return val.replace(/\D/g, "").length === 11;
    }),
  holderEmail: yup.string().email("Email inválido").notRequired(),
  holderPhone: yup
    .string()
    .required("Telefone do titular é obrigatório")
    .test("phone-digits", "Telefone deve ter 11 dígitos com DDD", (val) => {
      if (!val) return false;
      return val.replace(/\D/g, "").length === 11;
    }),
  postalCode: yup
    .string()
    .required("CEP do titular é obrigatório")
    .test("cep-digits", "CEP deve ter 8 dígitos", (val) => {
      if (!val) return false;
      return val.replace(/\D/g, "").length === 8;
    }),
  addressNumber: yup.string().notRequired(),
});

const formatCardNumber = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 16);
  return digits.replace(/(\d{4})(?=\d)/g, "$1 ");
};

const formatExpiryMonth = (value: string) => value.replace(/\D/g, "").slice(0, 2);

const formatExpiryYear = (value: string) => value.replace(/\D/g, "").slice(0, 4);

const formatCVV = (value: string) => value.replace(/\D/g, "").slice(0, 4);

const formatCPF = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  return digits
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
};

const formatPhone = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  return digits
    .replace(/(\d{2})(\d)/, "($1) $2")
    .replace(/(\d{5})(\d)/, "$1-$2");
};

const formatCEP = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 8);
  return digits.replace(/(\d{5})(\d)/, "$1-$2");
};

const CreditCardForm: React.FC<CreditCardFormProps> = ({ onSubmit, onBack, isLoading }) => {
  const { control, handleSubmit, formState: { errors, isValid } } = useForm<CreditCardData>({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues: {
      holderName: "",
      number: "",
      expiryMonth: "",
      expiryYear: "",
      ccv: "",
      cpfCnpj: "",
      holderEmail: "",
      holderPhone: "",
      postalCode: "",
      addressNumber: "",
    },
  });

  const handleFormSubmit = (data: CreditCardData) => {
    onSubmit({
      ...data,
      number: data.number.replace(/\s/g, ""),
      cpfCnpj: data.cpfCnpj.replace(/\D/g, ""),
      postalCode: data.postalCode.replace(/\D/g, ""),
      holderPhone: data.holderPhone.replace(/\D/g, ""),
    });
  };

  return (
    <Box component="form" onSubmit={handleSubmit(handleFormSubmit)} sx={{ py: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "var(--ink)" }}>
        Cartão de Crédito
      </Typography>

      <Controller
        name="holderName"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label="Nome no Cartão"
            fullWidth
            error={!!errors.holderName}
            helperText={errors.holderName?.message}
            sx={{ mb: 2 }}
            size="small"
          />
        )}
      />

      <Controller
        name="number"
        control={control}
        render={({ field: { onChange, value, ...rest } }) => (
          <TextField
            {...rest}
            value={value || ""}
            onChange={(e) => onChange(formatCardNumber(e.target.value))}
            label="Número do Cartão"
            error={!!errors.number}
            helperText={errors.number?.message}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
            inputProps={{ maxLength: 19 }}
          />
        )}
      />

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Controller
          name="expiryMonth"
          control={control}
          render={({ field: { onChange, value, ...rest } }) => (
            <TextField
              {...rest}
              value={value || ""}
              onChange={(e) => onChange(formatExpiryMonth(e.target.value))}
              label="Mês (MM)"
              error={!!errors.expiryMonth}
              helperText={errors.expiryMonth?.message}
              sx={{ flex: 1 }}
              size="small"
              inputProps={{ maxLength: 2 }}
            />
          )}
        />
        <Controller
          name="expiryYear"
          control={control}
          render={({ field: { onChange, value, ...rest } }) => (
            <TextField
              {...rest}
              value={value || ""}
              onChange={(e) => onChange(formatExpiryYear(e.target.value))}
              label="Ano (YYYY)"
              error={!!errors.expiryYear}
              helperText={errors.expiryYear?.message}
              sx={{ flex: 1 }}
              size="small"
              inputProps={{ maxLength: 4 }}
            />
          )}
        />
        <Controller
          name="ccv"
          control={control}
          render={({ field: { onChange, value, ...rest } }) => (
            <TextField
              {...rest}
              value={value || ""}
              onChange={(e) => onChange(formatCVV(e.target.value))}
              label="CVV"
              error={!!errors.ccv}
              helperText={errors.ccv?.message}
              sx={{ flex: 1 }}
              size="small"
              inputProps={{ maxLength: 4 }}
            />
          )}
        />
      </Stack>

      <Controller
        name="cpfCnpj"
        control={control}
        render={({ field: { onChange, value, ...rest } }) => (
          <TextField
            {...rest}
            value={value || ""}
            onChange={(e) => onChange(formatCPF(e.target.value))}
            label="CPF do Titular"
            error={!!errors.cpfCnpj}
            helperText={errors.cpfCnpj?.message}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
            inputProps={{ maxLength: 14 }}
          />
        )}
      />

      <Controller
        name="holderEmail"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label="Email"
            fullWidth
            error={!!errors.holderEmail}
            helperText={errors.holderEmail?.message}
            sx={{ mb: 2 }}
            size="small"
          />
        )}
      />

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Controller
          name="postalCode"
          control={control}
          render={({ field: { onChange, value, ...rest } }) => (
            <TextField
              {...rest}
              value={value || ""}
              onChange={(e) => onChange(formatCEP(e.target.value))}
              label="CEP"
              error={!!errors.postalCode}
              helperText={errors.postalCode?.message}
              sx={{ flex: 2 }}
              size="small"
              inputProps={{ maxLength: 9 }}
            />
          )}
        />
        <Controller
          name="addressNumber"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Número Endereço"
              sx={{ flex: 1 }}
              size="small"
            />
          )}
        />
      </Stack>

      <Controller
        name="holderPhone"
        control={control}
        render={({ field: { onChange, value, ...rest } }) => (
          <TextField
            {...rest}
            value={value || ""}
            onChange={(e) => onChange(formatPhone(e.target.value))}
            label="Telefone Celular"
            error={!!errors.holderPhone}
            helperText={errors.holderPhone?.message}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
            inputProps={{ maxLength: 15 }}
          />
        )}
      />

      <Stack direction="row" spacing={2} sx={{ mt: 3 }} justifyContent="flex-end">
        <Button variant="outlined" onClick={onBack} disabled={isLoading}>
          Voltar
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={!isValid || isLoading}
          sx={{ bgcolor: "var(--blue)", "&:hover": { bgcolor: "var(--blue-hover)" } }}
        >
          {isLoading ? <CircularProgress size={24} color="inherit" /> : "Confirmar Pagamento"}
        </Button>
      </Stack>
    </Box>
  );
};

export default CreditCardForm;
